import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { superadminApi, type ChangelogEntryDto, type ChangelogEntryType } from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ActionsMenu } from '@/components/shared/ActionsMenu'
import { Plus, Sparkles, Wrench, Bug, CheckCircle2, Pencil, Trash2, ClipboardList } from 'lucide-react'

const TYPES: ChangelogEntryType[] = ['Feature', 'Improvement', 'Fix']

const typeConfig: Record<ChangelogEntryType, { label: string; pill: string; icon: React.ReactNode }> = {
  Feature:     { label: 'Feature',     pill: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: <Sparkles size={12} /> },
  Improvement: { label: 'Improvement', pill: 'bg-blue-50 text-blue-700 border-blue-200',       icon: <Wrench size={12} /> },
  Fix:         { label: 'Fix',         pill: 'bg-amber-50 text-amber-700 border-amber-200',    icon: <Bug size={12} /> },
}

function fmtDate(d: string) {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

type FormData = {
  version: string
  date: string
  type: ChangelogEntryType
  title: string
  description: string
  isTested: boolean
  notes: string
}

function emptyForm(): FormData {
  return { version: '', date: new Date().toISOString().slice(0, 10), type: 'Feature', title: '', description: '', isTested: false, notes: '' }
}

function entryToForm(e: ChangelogEntryDto): FormData {
  return {
    version: e.version, date: e.date, type: e.type, title: e.title,
    description: e.description ?? '', isTested: e.isTested, notes: e.notes ?? '',
  }
}

// ─── Entry form modal ───────────────────────────────────────────────────────

function EntryModal({ initial, onClose, onSubmit, pending }: {
  initial: FormData
  onClose: () => void
  onSubmit: (data: FormData) => void
  pending: boolean
}) {
  const [form, setForm] = useState<FormData>(initial)
  const set = <K extends keyof FormData>(key: K, value: FormData[K]) => setForm(f => ({ ...f, [key]: value }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b">
          <h2 className="text-base font-semibold text-gray-900">{initial.version ? 'Edit entry' : 'New changelog entry'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none cursor-pointer">×</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSubmit(form) }} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="version">Version *</Label>
              <Input id="version" value={form.version} onChange={e => set('version', e.target.value)} placeholder="e.g. 1.8.2" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="date">Date *</Label>
              <Input id="date" type="date" value={form.date} onChange={e => set('date', e.target.value)} required />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Type</Label>
            <div className="flex gap-1 rounded-lg bg-gray-100 p-1 w-fit">
              {TYPES.map(t => (
                <button
                  key={t} type="button" onClick={() => set('type', t)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors cursor-pointer ${
                    form.type === t ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {typeConfig[t].label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="title">Title *</Label>
            <Input id="title" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Short summary" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description" value={form.description} onChange={e => set('description', e.target.value)}
              rows={3} placeholder="What changed, in plain language…"
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.isTested} onChange={e => set('isTested', e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            <span className="text-sm text-gray-700">Tested</span>
          </label>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Internal notes</Label>
            <textarea
              id="notes" value={form.notes} onChange={e => set('notes', e.target.value)}
              rows={2} placeholder="Not shown publicly — testing notes, caveats, follow-ups…"
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={pending}>{pending ? 'Saving…' : 'Save entry'}</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ChangelogPage() {
  const queryClient = useQueryClient()
  const [modal, setModal] = useState<'new' | ChangelogEntryDto | null>(null)

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['superadmin-changelog'],
    queryFn: () => superadminApi.getChangelog().then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (data: FormData) => superadminApi.createChangelogEntry({
      version: data.version.trim(), date: data.date, type: data.type,
      title: data.title.trim(), description: data.description.trim() || null,
      isTested: data.isTested, notes: data.notes.trim() || null,
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['superadmin-changelog'] }); setModal(null) },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) => superadminApi.updateChangelogEntry(id, {
      version: data.version.trim(), date: data.date, type: data.type,
      title: data.title.trim(), description: data.description.trim() || null,
      isTested: data.isTested, notes: data.notes.trim() || null,
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['superadmin-changelog'] }); setModal(null) },
  })

  const toggleTestedMutation = useMutation({
    mutationFn: (entry: ChangelogEntryDto) => superadminApi.updateChangelogEntry(entry.id, {
      version: entry.version, date: entry.date, type: entry.type, title: entry.title,
      description: entry.description, isTested: !entry.isTested, notes: entry.notes,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superadmin-changelog'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => superadminApi.deleteChangelogEntry(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superadmin-changelog'] }),
  })

  // Group consecutive entries by version, preserving the date-desc order from the API
  const groups: { version: string; date: string; entries: ChangelogEntryDto[] }[] = []
  for (const entry of entries) {
    const last = groups[groups.length - 1]
    if (last && last.version === entry.version) last.entries.push(entry)
    else groups.push({ version: entry.version, date: entry.date, entries: [entry] })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Changelog</h1>
          <p className="text-sm text-gray-500 mt-1">
            {entries.length} entr{entries.length === 1 ? 'y' : 'ies'} — internal for now, intended to eventually surface publicly.
          </p>
        </div>
        <Button onClick={() => setModal('new')}>
          <Plus size={15} className="mr-1.5" /> New entry
        </Button>
      </div>

      {isLoading && <p className="text-gray-400 text-sm">Loading…</p>}

      {!isLoading && groups.length === 0 && (
        <div className="text-center py-16 text-gray-400 bg-white border border-gray-200 rounded-xl">
          <ClipboardList size={32} className="mx-auto text-gray-200 mb-3" />
          No changelog entries yet.
        </div>
      )}

      <div className="space-y-6">
        {groups.map(group => (
          <div key={group.version} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
              <span className="font-mono text-sm font-semibold text-gray-900">v{group.version}</span>
              <span className="text-xs text-gray-400">{fmtDate(group.date)}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {group.entries.map(entry => (
                <div key={entry.id} className="flex items-start gap-3 px-5 py-3.5">
                  <span className={`inline-flex items-center gap-1 shrink-0 mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium border ${typeConfig[entry.type].pill}`}>
                    {typeConfig[entry.type].icon}
                    {typeConfig[entry.type].label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{entry.title}</p>
                    {entry.description && <p className="text-sm text-gray-500 mt-0.5 whitespace-pre-wrap">{entry.description}</p>}
                    {entry.notes && (
                      <p className="text-xs text-gray-400 mt-1.5 bg-gray-50 border border-gray-100 rounded px-2 py-1 whitespace-pre-wrap">
                        {entry.notes}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleTestedMutation.mutate(entry)}
                    disabled={toggleTestedMutation.isPending}
                    title={entry.isTested ? 'Tested — click to mark untested' : 'Not tested — click to mark tested'}
                    className={`inline-flex items-center gap-1 shrink-0 mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium border cursor-pointer transition-colors disabled:opacity-50 ${
                      entry.isTested
                        ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                        : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    <CheckCircle2 size={12} />
                    {entry.isTested ? 'Tested' : 'Untested'}
                  </button>
                  <ActionsMenu
                    items={[
                      {
                        label: 'Edit entry',
                        icon: <Pencil size={14} className="text-gray-500" />,
                        onClick: () => setModal(entry),
                      },
                      {
                        label: 'Delete entry',
                        icon: <Trash2 size={14} className="text-red-600" />,
                        onClick: () => { if (confirm(`Delete "${entry.title}"?`)) deleteMutation.mutate(entry.id) },
                        disabled: deleteMutation.isPending,
                        danger: true,
                      },
                    ]}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <EntryModal
          initial={modal === 'new' ? emptyForm() : entryToForm(modal)}
          onClose={() => setModal(null)}
          onSubmit={data => modal === 'new' ? createMutation.mutate(data) : updateMutation.mutate({ id: modal.id, data })}
          pending={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  )
}
